import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
// Assuming these API functions are correctly implemented in tailorApi.js
import { getCostumes, updateCostume } from '../api/tailorApi';

// Define the available costume types for the filter buttons
const costumeTypes = ['All', 'Shirt', 'Pant', 'Kurtha', 'Blouse'];

// --- Helper Components: Modal and Single View (No changes needed here, they are modal/fixed overlays) ---

/**
 * Modal for editing costume details.
 */
const EditCostumeModal = ({ costume, onClose, onSave }) => {
    // Initialize form data, ensuring ID is always present even if it's null initially
    const [formData, setFormData] = useState(costume);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        setSaveError(null);

        // --- CRITICAL FIX: Ensure the API key 'costume_id' is sent as a Number ---

        // 1. Destructure the local 'id' out of the form data to prevent it from being sent as 'id'.
        const { id: localId, ...restOfFormData } = formData;

        const payload = {
            // Spread the rest of the data (e.g., name, code, category, description)
            ...restOfFormData,

            // 2. Map the local 'id' to the API's required key name: 'costume_id'.
            // 3. Explicitly convert the ID to a numeric type, fixing the 'A numeric costume_id is required' error.
            costume_id: Number(localId),

            // 4. Explicitly convert all other numeric/decimal/integer fields to numbers, as per API documentation.
            base_price: Number(restOfFormData.base_price),
            urgent_price: Number(restOfFormData.urgent_price),
            normal_delivery_days: Number(restOfFormData.normal_delivery_days),
            urgent_delivery_days: Number(restOfFormData.urgent_delivery_days),
        };
        // -------------------------------------------------------------------------

        try {
            await onSave(payload); // Pass the correctly formatted payload
            onClose(); // Close modal on success
        } catch (error) {
            // Error handling for the save operation
            setSaveError(error.message || "Failed to update costume. Check API response for details.");
        } finally {
            setSaveLoading(false);
        }
    };

    const inputClass = "w-full p-2 border border-gray-300 rounded-lg focus:ring-slate-500 focus:border-slate-500";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden transform transition-all">

                <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold">Edit Costume: {costume.costume_name}</h2>
                    <button onClick={onClose} className="text-white hover:text-gray-300 p-1 rounded-full">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <form onSubmit={handleSave} className="p-6 space-y-4">
                    {/* Name and Code */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass} htmlFor="costume_name">Name</label>
                            <input type="text" id="costume_name" name="costume_name" value={formData.costume_name || ''} onChange={handleChange} className={inputClass} required />
                        </div>
                        <div>
                            <label className={labelClass} htmlFor="costume_code">Code</label>
                            <input type="text" id="costume_code" name="costume_code" value={formData.costume_code || ''} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    {/* Type and Category */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass} htmlFor="costume_type">Type</label>
                            <select id="costume_type" name="costume_type" value={formData.costume_type || ''} onChange={handleChange} className={inputClass} required>
                                {costumeTypes.filter(t => t !== 'All').map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass} htmlFor="category">Category</label>
                            <input type="text" id="category" name="category" value={formData.category || ''} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className={labelClass} htmlFor="description">Description</label>
                        <textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} className={`${inputClass} h-20`} />
                    </div>

                    <h3 className="text-md font-semibold text-slate-900 mt-4 border-b pb-1">Pricing & Delivery</h3>
                    {/* Pricing */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass} htmlFor="base_price">Base Price (₹)</label>
                            <input type="text" id="base_price" name="base_price" value={formData.base_price || ''} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass} htmlFor="urgent_price">Urgent Price (₹)</label>
                            <input type="text" id="urgent_price" name="urgent_price" value={formData.urgent_price || ''} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    {/* Delivery Days */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass} htmlFor="normal_delivery_days">Normal Days</label>
                            <input type="text" id="normal_delivery_days" name="normal_delivery_days" value={formData.normal_delivery_days || ''} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass} htmlFor="urgent_delivery_days">Urgent Days</label>
                            <input type="text" id="urgent_delivery_days" name="urgent_delivery_days" value={formData.urgent_delivery_days || ''} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    {/* Error Message */}
                    {saveError && (
                        <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">{saveError}</div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition duration-150">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-700 transition duration-150 disabled:opacity-50"
                            disabled={saveLoading}
                        >
                            {saveLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Single Costume View Component ---
/**
 * Single Card View for a costume.
 */
const SingleCostumeView = ({ costume, onClose }) => {
    const {
        costume_name, costume_code, costume_type, category, description,
        base_price, urgent_price, normal_delivery_days, urgent_delivery_days
    } = costume;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden transform transition-all">

                {/* Header */}
                <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold">Costume Details: {costume_name}</h2>
                    <button onClick={onClose} className="text-white hover:text-gray-300 p-1 rounded-full">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* General Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm font-medium text-gray-500">Name</p>
                            <p className="text-lg font-semibold text-gray-800">{costume_name}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Code</p>
                            <p className="text-lg font-semibold text-gray-800">{costume_code}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Type</p>
                            <p className="text-lg font-semibold text-gray-800">{costume_type}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Category</p>
                            <p className="text-lg font-semibold text-gray-800">{category || 'N/A'}</p>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <p className="text-sm font-medium text-gray-500">Description</p>
                        <p className="text-md text-gray-700 italic">{description || 'No description provided.'}</p>
                    </div>


                    {/* Pricing */}
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h3 className="text-md font-semibold text-green-700 mb-3">💲 Pricing</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Base Price</p>
                                <p className="text-lg font-bold text-green-600">₹{base_price || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Urgent Price</p>
                                <p className="text-lg font-bold text-red-600">₹{urgent_price || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Delivery Time */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h3 className="text-md font-semibold text-blue-700 mb-3">🕓 Delivery Time</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Normal</p>
                                <p className="text-lg font-bold text-blue-600">{normal_delivery_days ? `${normal_delivery_days} days` : 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Urgent</p>
                                <p className="text-lg font-bold text-blue-600">{urgent_delivery_days ? `${urgent_delivery_days} days` : 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex justify-end pt-4">
                        <button onClick={onClose} className="px-6 py-2 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-700 transition duration-150">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Component: CostumeListing ---

function CostumeListing() {
    const [costumes, setCostumes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeType, setActiveType] = useState('All');

    // State for Modals
    const [editingCostume, setEditingCostume] = useState(null);
    const [viewingCostume, setViewingCostume] = useState(null);

    // Function to fetch data and update state
    const fetchAndSetCostumes = async () => {
        try {
            setLoading(true);
            const data = await getCostumes();

            if (Array.isArray(data)) {
                // Ensure number fields are consistently parsed as numbers for filtering/display
                const parsedData = data.map(c => ({
                    ...c,
                    // Use parseFloat/parseInt for safe conversion, defaulting to 0 if invalid
                    base_price: c.base_price ? parseFloat(c.base_price) : 0,
                    urgent_price: c.urgent_price ? parseFloat(c.urgent_price) : 0,
                    normal_delivery_days: c.normal_delivery_days ? parseInt(c.normal_delivery_days) : 0,
                    urgent_delivery_days: c.urgent_delivery_days ? parseInt(c.urgent_delivery_days) : 0,
                }));
                setCostumes(parsedData);
            } else {
                setCostumes([]);
                throw new Error("API returned unexpected data format.");
            }
            setError(null);
        } catch (e) {
            console.error("Failed to load costumes:", e);
            setError(e.message || "Failed to load costumes. Check network or server configuration (CORS).");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAndSetCostumes();
    }, []);

    // API Save Handler for Edit Modal
    const handleSaveCostume = async (updatedData) => {
        try {
            // updatedData now contains numeric costume_id and correctly typed fields from the modal's logic
            await updateCostume(updatedData);
            // Refresh the entire list after a successful update
            await fetchAndSetCostumes();
        } catch (e) {
            // Re-throw the error to be caught by the modal component for displaying save error
            throw e;
        }
    };

    // --- Filtering and Search Logic using useMemo ---
    const filteredCostumes = useMemo(() => {
        let result = costumes;

        // 1. Filter by Type
        if (activeType !== 'All') {
            result = result.filter(costume => costume.costume_type === activeType);
        }

        // 2. Filter by Search Term (Case-insensitive check across name, code, or type)
        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            result = result.filter(costume =>
                costume.costume_name?.toLowerCase().includes(lowerCaseSearch) ||
                costume.costume_code?.toLowerCase().includes(lowerCaseSearch) ||
                costume.costume_type?.toLowerCase().includes(lowerCaseSearch)
            );
        }

        return result;
    }, [costumes, activeType, searchTerm]);

    // --- Render Logic for Loading/Errors/No Data (Tailwind Styles) ---

    const renderContent = (content) => (
        <div className="flex-grow p-4 md:p-6 bg-gray-50 min-h-screen">
            {content}
        </div>
    );

    if (loading && costumes.length === 0) {
        return renderContent(
            <div className="text-center p-12 text-lg font-semibold text-gray-700">
                <div className="animate-spin inline-block w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full mr-3"></div>
                Loading all costume data...
            </div>
        );
    }

    if (error) {
        return renderContent(
            <div className="text-center p-12 text-2xl font-bold bg-red-100 text-red-700 border border-red-400 rounded-lg mx-auto w-full max-w-4xl my-5">
                ❌ Error: {error}
            </div>
        );
    }


    // --- Render Costumes Grid with Search and Filters ---

    return (
        <div>
            <div className="p-4 md:p-6 font-sans bg-white text-gray-800 min-h-screen">
                {/* 🚨 ADJUSTMENT 1: Decreased default padding from p-6 to p-4 for mobile space */}

                {/* Header and Title Area */}
                <div className="mb-6 border-b pb-4">
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Costume Listing</h1>
                    {/* 🚨 ADJUSTMENT 2: Reduced title size slightly for mobile */}
                    <p className="text-sm text-gray-500 mt-1">Manage and view all the costumes details.</p>
                </div>

                {/* --- Search Bar and Filters Area --- */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">

                    {/* Search Input */}
                    <div className="relative flex items-center w-full md:w-2/3 lg:w-1/2 mb-4 md:mb-0">
                        <svg className="w-5 h-5 text-gray-400 absolute left-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                        <input
                            type="text"
                            placeholder="Search by name, code, or type..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-slate-500 focus:border-slate-500 transition duration-150 text-sm"
                        // 🚨 ADJUSTMENT 3: Added text-sm for better fit on mobile keyboards
                        />
                    </div>

                    {/* Filter Buttons */}
                    <div className="flex space-x-2 overflow-x-auto p-1 border border-gray-300 rounded-lg bg-white shadow-inner w-full md:w-auto">
                        {/* 🚨 ADJUSTMENT 4: w-full ensures filters take up full width on mobile */}
                        {costumeTypes.map((type) => (
                            <button
                                key={type}
                                onClick={() => setActiveType(type)}
                                className={`
                                    px-3 py-1 text-sm font-medium rounded-md whitespace-nowrap transition duration-150
                                    ${activeType === type
                                        ? 'bg-slate-900 text-white shadow-md'
                                        : 'bg-white text-gray-700 hover:bg-gray-100'
                                    }
                                `}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </div>
                {/* --- End Search Bar and Filters Area --- */}

                {/* Check if filtered results exist */}
                {filteredCostumes.length === 0 ? (
                    <div className="text-center p-8 md:p-12 text-lg font-medium text-gray-600 bg-white border border-gray-200 rounded-xl shadow-lg">
                        No costumes match your current filter and search criteria.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {/* The grid is already responsive: 1 column on mobile, 2 on small screens, etc. */}
                        {filteredCostumes.map((costume) => (
                            <div
                                key={costume.id}
                                className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 flex flex-col transition hover:shadow-xl"
                            // 🚨 ADJUSTMENT 5: Reduced card padding from p-5 to p-4
                            >

                                {/* Costume Header: Name, Code, and Type badge */}
                                <div className="flex justify-between items-start mb-4 border-b pb-3 border-gray-100">
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 bg-slate-50 rounded-full flex items-center justify-center mr-3 text-slate-600 border border-slate-100">
                                            👕 {/* Reduced icon container size slightly */}
                                        </div>
                                        <div>
                                            <h2 className="text-base font-bold text-gray-900 leading-tight"> {/* Reduced text size */}
                                                {costume.costume_name || 'Untitled Costume'}
                                            </h2>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Code: {costume.costume_code}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="text-xs font-semibold bg-slate-900 text-white px-3 py-1 rounded-full whitespace-nowrap self-start">
                                        {costume.costume_type}
                                    </span>
                                </div>

                                {/* Category and ID */}
                                <div className="text-sm text-gray-600 mb-4 space-y-1">
                                    <p><strong className="font-semibold text-gray-800">Category:</strong> {costume.category || 'N/A'}</p>
                                    <p><strong className="font-semibold text-gray-800">ID:</strong> {costume.id}</p>
                                </div>

                                {/* Pricing Section (Green box) */}
                                <div className="bg-green-50 p-3 rounded-lg mb-4 border border-green-200">
                                    <p className="text-sm font-semibold text-green-700 mb-2">💲 Pricing</p>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="text-left pr-2">
                                            <p className="text-xs text-gray-500">Base Price</p>
                                            <p className="font-bold text-green-600 text-base">
                                                {costume.base_price ? `₹${costume.base_price}` : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="text-right pl-2 border-l border-green-200">
                                            <p className="text-xs text-gray-500">Urgent Price</p>
                                            <p className="font-bold text-red-600 text-base">
                                                {costume.urgent_price ? `₹${costume.urgent_price}` : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Delivery Time Section (Blue box) */}
                                <div className="bg-blue-50 p-3 rounded-lg mb-4 border border-blue-200">
                                    <p className="text-sm font-semibold text-blue-700 mb-2">🕓 Delivery Time</p>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="text-left pr-2">
                                            <p className="text-xs text-gray-500">Normal</p>
                                            <p className="font-bold text-blue-600 text-base">
                                                {costume.normal_delivery_days ? `${costume.normal_delivery_days} days` : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="text-right pl-2 border-l border-blue-200">
                                            <p className="text-xs text-gray-500">Urgent</p>
                                            <p className="font-bold text-blue-600 text-base">
                                                {costume.urgent_delivery_days ? `${costume.urgent_delivery_days} days` : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Order Totals (Placeholder) */}
                                <div className="flex justify-around items-center border-t pt-3 mt-auto mb-4 text-sm">
                                    <div className="text-center">
                                        <p className="text-xs text-gray-500">Total Orders</p>
                                        <p className="font-bold text-gray-800 text-xl">45</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-500">Active</p>
                                        <p className="font-bold text-red-600 text-xl">8</p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between space-x-3 pt-2">
                                    <button
                                        onClick={() => setEditingCostume(costume)}
                                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-semibold py-2 rounded-lg transition duration-150 shadow-sm"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => setViewingCostume(costume)}
                                        className="flex-1 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold py-2 rounded-lg transition duration-150 shadow-md"
                                    >
                                        View Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Render Modals */}
            {editingCostume && (
                <EditCostumeModal
                    costume={editingCostume}
                    onClose={() => setEditingCostume(null)}
                    onSave={handleSaveCostume}
                />
            )}

            {viewingCostume && (
                <SingleCostumeView
                    costume={viewingCostume}
                    onClose={() => setViewingCostume(null)}
                />
            )}
        </div>
    );
}

export default CostumeListing;